import { parseISO } from "date-fns";
import { ArrowUpRight, Braces, List, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { JsonTable } from "~/components/json-table";
import { SectionCard } from "~/components/section-card";
import { Badge } from "~/components/ui";
import type { EntityDetailUI, EntityGeoItemRow, EntityPropertyGroup } from "~/features/entities/types";
import { v2Route } from "~/v2/routes";
import { EntityCompoundEntityCard } from "./EntityCompoundEntityCard";
import { EntityPropertyValue } from "./EntityPropertyValue";
import { EntityRelationList } from "./EntityRelationList";

interface EntityDataTabProps {
  detail: EntityDetailUI;
  onSelectEntity: (id: string) => void;
}

export function EntityDataTab({ detail, onSelectEntity }: EntityDataTabProps) {
  const { t } = useTranslation();
  const hasOtherProperties = Object.keys(detail.otherProperties).length > 0;

  return (
    <div className="space-y-5">
      {/* Category type + date range, sitting directly above the property table (V1 parity). */}
      <div className="flex flex-wrap items-center gap-3">
        {detail.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {detail.categories.map((c) => (
              <Link key={c.id} to={`/admin/categories/${c.id}`} className="rounded-[var(--radius-pill)]">
                <Badge tone="default" className="inline-flex cursor-pointer items-center gap-1 hover:bg-bg-tint">
                  {c.label}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Badge>
              </Link>
            ))}
          </div>
        )}
        {detail.dateRange && (
          <>
            <span className="h-4 w-px bg-border-default" aria-hidden />
            <span className="text-14-regular tabular-nums text-fg-2">{detail.dateRange}</span>
          </>
        )}
      </div>

      {detail.propertyGroups.map((group) => (
        <PropertyGroupCard key={group.categoryId} group={group} />
      ))}

      {hasOtherProperties && (
        <SectionCard icon={Braces} title={t("feature.entities.detail.otherProperties")}>
          <JsonTable target={detail.otherProperties} />
        </SectionCard>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <EntityRelationList
          title={t("model.category.relation.sourceRelations")}
          relations={detail.sourceRelations}
          onSelectEntity={onSelectEntity}
        />
        <EntityRelationList
          title={t("model.category.relation.targetRelations")}
          relations={detail.targetRelations}
          onSelectEntity={onSelectEntity}
        />
      </div>

      <EntityCompoundEntityCard compoundEntity={detail.compoundEntity} />

      <SectionCard icon={Braces} title={t("model.common.metadata")}>
        <JsonTable target={detail.metadata} />
      </SectionCard>

      <GeoItemsCard entityId={detail.id} rows={detail.geoItems} />
    </div>
  );
}

function PropertyGroupCard({ group }: { group: EntityPropertyGroup }) {
  const { t } = useTranslation();

  return (
    <SectionCard icon={List} title={group.categoryLabel} capitalizeTitle={false} contentClassName="overflow-x-auto p-0">
      {group.rows.length === 0 ? (
        <p className="px-6 pb-4 text-[13px] text-fg-3">{t("feature.common.noData")}</p>
      ) : (
        <table className="w-full border-collapse text-[13px]">
          <tbody>
            {group.rows.map((row) => (
              <tr key={row.name} className="border-b border-border-default align-top last:border-b-0">
                <td className="w-12 whitespace-nowrap px-3.5 py-2.5 text-right align-top font-bold tabular-nums text-fg-3">
                  {row.position}
                </td>
                <th className="whitespace-nowrap px-3.5 py-2.5 text-left align-top font-bold text-fg-1">{row.name}</th>
                <td className="px-3.5 py-2.5 text-fg-2">
                  <EntityPropertyValue
                    value={row.value}
                    propertyTypeId={row.propertyTypeId}
                    selectOptions={row.selectOptions}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

function GeoItemsCard({ entityId, rows }: { entityId: string; rows: EntityGeoItemRow[] }) {
  const { t } = useTranslation();

  return (
    <SectionCard
      icon={MapPin}
      title={t("model.geoItem.modelNames")}
      contentClassName={rows.length === 0 ? undefined : "overflow-x-auto p-0"}
    >
      {rows.length === 0 ? (
        <p className="text-[13px] text-fg-3">{t("feature.common.noData")}</p>
      ) : (
        <table className="w-full min-w-max border-collapse text-[13px]">
          <thead>
            <tr className="bg-bg-subtle">
              <Th>{t("model.common.id")}</Th>
              <Th>{t("ui.date")}</Th>
              <Th>{t("model.geoItem.endDate")}</Th>
              <Th>{t("model.geoItem.i18nLabel")}</Th>
              <Th>{t("model.recordGeoItem.heading")}</Th>
              <Th>{t("model.geoItem.countryCodes")}</Th>
              <Th>{t("model.common.deactivatedAt")}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((gi, index) => (
              // A GeoItem appears once per link entry, so its ID alone does not identify a row.
              <tr key={`${gi.id}:${index}`} className="border-b border-border-default last:border-b-0">
                <td className="whitespace-nowrap px-3.5 py-2.5 text-fg-2">
                  <span className="inline-flex items-center gap-1.5 tabular-nums">
                    {gi.id}
                    <Link
                      to={v2Route.areaInput.record.detail.$buildPath({
                        params: { recordId: entityId, geoItemId: gi.id },
                      })}
                      aria-label={t("ui.detail")}
                      className="text-fg-link"
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </span>
                </td>
                <td className="whitespace-nowrap px-3.5 py-2.5 tabular-nums text-fg-2">{gi.date || "—"}</td>
                <td className="whitespace-nowrap px-3.5 py-2.5 tabular-nums text-fg-2">{gi.endDate || "—"}</td>
                <td className="px-3.5 py-2.5 text-fg-1">{gi.label}</td>
                <td className="px-3.5 py-2.5 text-fg-2">{gi.heading ?? "—"}</td>
                <td className="px-3.5 py-2.5 text-fg-2">{gi.countryIds.join(", ") || "—"}</td>
                <td className="whitespace-nowrap px-3.5 py-2.5 tabular-nums text-fg-2">
                  {gi.deactivatedAt ? t("date.long", { date: parseISO(gi.deactivatedAt) }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2">
      {children}
    </th>
  );
}
